import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { DesignPhotos } from "@/components/catalog/DesignPhotos";
import { ComboPicker } from "@/components/catalog/ComboPicker";

const COLOR_KEYS = ["min_colors", "max_colors"];
// ID-069: whole numbers only, min ≥ 1, max ≥ min — never silently rounded
const colorErrors = (f) => {
  const e = {};
  COLOR_KEYS.forEach((k) => { if (!/^\d+$/.test(String(f[k]).trim())) e[k] = "Enter a whole number (no decimals)"; });
  if (!e.min_colors && Number(f.min_colors) < 1) e.min_colors = "Minimum must be at least 1";
  if (!e.max_colors && Number(f.max_colors) > 8) e.max_colors = "Maximum is 8";
  if (!e.min_colors && !e.max_colors && Number(f.max_colors) < Number(f.min_colors)) e.max_colors = "Maximum must be greater than or equal to minimum";
  return e;
};

const LEVELS = ["standard", "preferred", "premier", "strategic"];
const REQUIRED = [["name", "Design name"], ["category", "Family / category"], ["design_id", "Design identifier"], ["sku_prefix", "SKU prefix"], ["dimensions", "Dimensions"], ["sqft_per_box", "Sq ft / box"]];
// ID-071: every reason Preview SKUs cannot run, by field — never a silent no-op
const formErrors = (f, combos, draft) => {
  const out = REQUIRED.filter(([k]) => !String(f[k] ?? "").trim()).map(([, l]) => `${l}: required`);
  const ce = colorErrors(f);
  Object.entries(ce).forEach(([k, m]) => out.push(`${k === "min_colors" ? "Min" : "Max"} colors / variation: ${m}`));
  const ok = !Object.keys(ce).length;
  const lo = Number(f.min_colors);
  const hi = Number(f.max_colors);
  const need = ok ? (lo === hi ? `${lo}` : `${lo}–${hi}`) : "the allowed number of";
  if (!combos.length) out.push(draft.length ? `Color combinations: ${draft.join(" + ")} is not added yet — pick ${need} colors and click Add combination` : "Color combinations: add at least one combination");
  if (ok) combos.forEach((c) => { if (c.length < lo || c.length > hi) out.push(`Color combinations: ${c.join(" + ")} has ${c.length} colors — needs ${lo === hi ? lo : `${lo}–${hi}`}`); });
  const given = LEVELS.filter((l) => String(f[l] ?? "").trim() !== "");
  if (given.length && given.length < 4) out.push(`Selling prices: enter all four tiers or leave all blank (missing ${LEVELS.filter((l) => !given.includes(l)).join(", ")})`);
  given.forEach((l) => { if (!(Number(f[l]) > 0)) out.push(`${l[0].toUpperCase()}${l.slice(1)} $/box: must be above $0`); });
  return out;
};
const TEXT = [["name", "Design name *"], ["category", "Family / category *"], ["design_id", "Design identifier *"], ["sku_prefix", "SKU prefix *"],
  ["pattern", "Pattern"], ["dimensions", "Dimensions *"], ["mesh_sheet_dims", "Mesh sheet size"], ["finish", "Finish"]];
const NUM = [["pieces_per_sheet", "Pieces / sheet"], ["sheets_per_box", "Sheets / box"], ["sqft_per_box", "Sq ft / box *"],
  ["boxes_per_pallet", "Boxes / pallet"], ["min_colors", "Min colors / variation"], ["max_colors", "Max colors / variation"], ["supplier_cost", "Supplier cost $/box"], ["inbound_freight_alloc", "Freight/Shipping $/box"],
  ...LEVELS.map((l) => [l, `${l[0].toUpperCase()}${l.slice(1)} $/box`])];
const EMPTY = { ...Object.fromEntries([...TEXT, ...NUM].map(([k]) => [k, ""])), category: "Mosaic", presentation: "Mesh-mounted", supplier_id: "", tech_specs: "", min_colors: "1", max_colors: "8", composition_mode: "OPTIONAL" };
const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k${Date.now()}${Math.random()}`);
const Field = ({ label, children }) => <div className="space-y-1"><Label className="text-[11px] font-semibold uppercase tracking-wider text-warmgrey">{label}</Label>{children}</div>;

function payload(f, combos, key, dry) {
  const n = (v) => (v === "" ? null : Number(v));
  return {
    ...Object.fromEntries(TEXT.map(([k]) => [k, f[k].trim()])), presentation: f.presentation, supplier_id: f.supplier_id || null, composition_mode: f.composition_mode,
    ...Object.fromEntries(NUM.filter(([k]) => !LEVELS.includes(k)).map(([k]) => [k, n(f[k])])),
    prices: Object.fromEntries(LEVELS.map((l) => [l, n(f[l])])), tech_specs: f.tech_specs,
    combinations: combos.map((colors) => ({ colors })),
    idempotency_key: key, dry_run: dry,
  };
}

// ID-060: + New Product Design — mosaics/specialty designs in explicitly selected colors only
export function NewDesignDialog({ open, onOpenChange, onCreated, prefill, draftKey }) {
  const [f, setF] = useState(EMPTY);
  const [suppliers, setSuppliers] = useState([]);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(newKey);
  const [photos, setPhotos] = useState([]);
  const [combos, setCombosRaw] = useState([]);
  const [draft, setDraft] = useState([]);
  const [colors, setColors] = useState([]);
  const [problems, setProblems] = useState([]);
  const msgRef = useRef(null);
  const setCombos = (fn) => { setCombosRaw(fn); setCheck(null); setProblems([]); };
  const errs = colorErrors(f);
  const invalid = formErrors(f, combos, draft).length > 0;
  useEffect(() => {
    if (!open) { return; }
    setPhotos([]);
    setCombosRaw([]);
    setDraft([]);
    setProblems([]);
    api.get("/products").then(({ data }) => setColors((data.products || data).filter((p) => p.color_code && p.active && !p.archived && p.family_group !== "MOSAIC_COLLECTION"))).catch(() => setColors([]));
    setF({ ...EMPTY, ...Object.fromEntries(Object.entries(prefill || {}).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => [k, String(v)])) }); setCheck(null); setKey(newKey());
    try { const s = draftKey && JSON.parse(localStorage.getItem(draftKey) || "null"); if (s) { setF((x) => ({ ...x, ...s.f })); setCombosRaw(s.combos || []); } } catch { /* ignore bad draft */ }
    api.get("/admin/suppliers").then(({ data }) => setSuppliers(Array.isArray(data) ? data : data.suppliers || [])).catch(() => setSuppliers([]));
  }, [open, prefill]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && draftKey) { localStorage.setItem(draftKey, JSON.stringify({ f, combos })); } }, [open, draftKey, f, combos]); // ID-076: import design inputs survive refresh
  const set = (k) => (e) => { setF((x) => ({ ...x, [k]: e.target.value })); setCheck(null); setProblems([]); };
  const show = (list) => { setProblems(list); setTimeout(() => msgRef.current?.scrollIntoView({ block: "nearest" }), 0); };
  const uploadPhotos = async (designId) => {
    let failed = 0;
    for (const p of photos) {
      const fd = new FormData();
      fd.append("file", p.file);
      try { await api.post(`/admin/catalog/designs/${encodeURIComponent(designId)}/images`, fd); } catch { failed += 1; }
    }
    if (failed) toast.error(`${failed} photo(s) could not be saved — add them from the product page`);
  };
  const run = async (dry) => {
    const list = formErrors(f, combos, draft);
    if (list.length) { show(list); return; }
    setProblems([]);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/catalog/designs", payload(f, combos, key, dry));
      if (dry) { setCheck(data); setTimeout(() => msgRef.current?.scrollIntoView({ block: "nearest" }), 0); return; }
      if (photos.length) await uploadPhotos(data.design_id || f.design_id.trim());
      toast.success(`${f.name} created: ${data.skus.join(", ")}${data.priced ? "" : " — unpriced until set in Pricing Engine"}`);
      onOpenChange(false); if (draftKey) localStorage.removeItem(draftKey); onCreated?.({ ...data, design_id: data.design_id || f.design_id.trim(), name: f.name.trim() });
    } catch (e) {
      const d = e.response?.data?.detail;
      const labels = Object.fromEntries([...TEXT, ...NUM, ["combinations", "Color combinations"]].map(([k, l]) => [k, l.replace(" *", "")]));
      const list = Array.isArray(d) ? d.map((x) => `${labels[x.loc?.[1]]?.replace(" *", "") || x.loc?.slice(1).join(" ") || "Form"}: ${x.msg}`) : [formatApiErrorDetail(d) || "Could not reach the server — try again"];
      show(list);
      toast.error(list[0]);
    }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-white" data-testid="new-design-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">New product design</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">Mosaics and other specialty designs. Only the color combinations you list are created — nothing propagates to other colors. Pricing uses the same Pricing Engine tiers; leave prices blank if they are not set yet.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {TEXT.map(([k, l]) => <Field key={k} label={l}><Input value={f[k]} onChange={set(k)} autoComplete="off" data-testid={`design-${k}`} className="h-10 bg-white" /></Field>)}
          <Field label="Presentation">
            <select value={f.presentation} onChange={set("presentation")} data-testid="design-presentation" className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm"><option>Mesh-mounted</option><option>Loose</option></select>
          </Field>
          <Field label="Color % in requests">
            <select value={f.composition_mode} onChange={set("composition_mode")} data-testid="design-composition-mode" className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
              <option value="OPTIONAL">Optional</option><option value="REQUIRED">Required</option><option value="NONE">Not used</option>
            </select>
          </Field>
          <Field label="Supplier">
            <select value={f.supplier_id} onChange={set("supplier_id")} data-testid="design-supplier" className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
              <option value="">—</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          {NUM.map(([k, l]) => (
            <Field key={k} label={l}>
              <Input type="number" step={COLOR_KEYS.includes(k) ? "1" : "0.01"} min={COLOR_KEYS.includes(k) ? "1" : "0"} value={f[k]} onChange={set(k)} data-testid={`design-${k}`}
                aria-invalid={!!errs[k]} className={`h-10 bg-white ${errs[k] ? "border-terracotta" : ""}`} />
              {errs[k] && <p className="text-xs text-terracotta" data-testid={`design-${k}-error`}>{errs[k]}</p>}
            </Field>
          ))}
        </div>
        <Field label="Color combinations * — each combination becomes one variation SKU">
          <ComboPicker colors={colors} combos={combos} setCombos={setCombos} draft={draft} setDraft={(fn) => { setDraft(fn); setProblems([]); }} min={errs.min_colors ? 1 : f.min_colors} max={errs.max_colors ? 8 : f.max_colors} />
        </Field>
        <Field label="Technical specifications"><Textarea rows={2} value={f.tech_specs} onChange={set("tech_specs")} data-testid="design-tech-specs" className="bg-white" /></Field>
        <Field label="Upload photos"><DesignPhotos photos={photos} setPhotos={setPhotos} /></Field>
        <div ref={msgRef} />
        {problems.length > 0 && (
          <div className="rounded-md border border-terracotta/50 bg-terracotta/5 p-3 text-xs text-charcoal" role="alert" data-testid="design-errors">
            <p className="font-semibold text-terracotta">Preview SKUs could not run:</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          </div>
        )}
        {check && <p className="rounded-md border border-border bg-sand/30 p-3 text-xs" data-testid="design-preview">Will create {check.skus.length} SKU(s): {check.skus.join(", ")} · {check.priced ? "priced" : "unpriced"} · status {"inactive"}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={() => run(true)} data-testid="design-check">{busy && !check ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview SKUs"}</Button>
          <Button disabled={busy || !check || invalid} onClick={() => run(false)} data-testid="design-submit" className="bg-terracotta text-white hover:bg-terracotta-hover">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create design"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
